import { DateTime } from 'luxon'
import { amplitudeEvent } from 'lib/amplitude'

export const HOLIDAY_LESSON_PACK_EVENTS = {
  BANNER_VIEWED: 'holiday-lesson-pack-banner-viewed',
  CTA_CLICKED: 'holiday-lesson-pack-banner-cta-clicked',
  BANNER_DISMISSED: 'holiday-lesson-pack-banner-dismissed',
  LESSON_PACK_CREATED: 'holiday-lesson-pack-created-after-banner-view',
}

// October through December, so coaches have time to sell packs as gifts.
const SEASON_START_MONTH = 10

export const getHolidaySeason = (now = DateTime.local()) =>
  now.month >= SEASON_START_MONTH ? `holiday-${now.year}` : null

const storageKey = (season, userId) =>
  `pgaCoach.holidayLessonPackBanner.${season}.${userId}`

export const readHolidayBannerRecord = (season, userId) => {
  try {
    return (
      JSON.parse(window.localStorage.getItem(storageKey(season, userId))) ?? {}
    )
  } catch (e) {
    return {}
  }
}

const updateHolidayBannerRecord = (season, userId, updates) => {
  const record = { ...readHolidayBannerRecord(season, userId), ...updates }
  try {
    window.localStorage.setItem(
      storageKey(season, userId),
      JSON.stringify(record),
    )
  } catch (e) {
    // Storage can be unavailable (private mode, quotas); later lesson pack
    // creation then can't be attributed to the banner.
  }
  return record
}

const hoursSince = isoDateTime =>
  Math.round(
    DateTime.local()
      .diff(DateTime.fromISO(isoDateTime), 'hours')
      .as('hours'),
  )

export const trackHolidayBannerViewed = ({ season, userId }) => {
  const { firstViewedAt } = readHolidayBannerRecord(season, userId)
  if (!firstViewedAt) {
    updateHolidayBannerRecord(season, userId, {
      firstViewedAt: DateTime.local().toISO(),
    })
  }
  amplitudeEvent(HOLIDAY_LESSON_PACK_EVENTS.BANNER_VIEWED, {
    season,
    is_first_view: !firstViewedAt,
    path: window.location.pathname,
  })
}

export const trackHolidayBannerCtaClicked = ({
  season,
  userId,
  destination,
}) => {
  const { ctaClickedAt } = readHolidayBannerRecord(season, userId)
  if (!ctaClickedAt) {
    updateHolidayBannerRecord(season, userId, {
      ctaClickedAt: DateTime.local().toISO(),
    })
  }
  amplitudeEvent(HOLIDAY_LESSON_PACK_EVENTS.CTA_CLICKED, {
    season,
    destination,
    path: window.location.pathname,
  })
}

export const trackHolidayBannerDismissed = ({ season, userId }) => {
  updateHolidayBannerRecord(season, userId, {
    dismissedAt: DateTime.local().toISO(),
  })
  amplitudeEvent(HOLIDAY_LESSON_PACK_EVENTS.BANNER_DISMISSED, {
    season,
    path: window.location.pathname,
  })
}

// Fires for any lesson pack created after the coach saw the banner this
// season, whether or not they used the banner's button to get there.
export const trackLessonPackCreatedAfterHolidayBanner = ({
  userId,
  lessonTypeId,
  lessonPackCount,
}) => {
  const season = getHolidaySeason()
  if (!season) return

  const record = readHolidayBannerRecord(season, userId)
  if (!record.firstViewedAt) return

  updateHolidayBannerRecord(season, userId, {
    lessonPackCreatedAt: DateTime.local().toISO(),
  })
  amplitudeEvent(HOLIDAY_LESSON_PACK_EVENTS.LESSON_PACK_CREATED, {
    season,
    clicked_cta: Boolean(record.ctaClickedAt),
    hours_since_first_view: hoursSince(record.firstViewedAt),
    hours_since_cta_click: record.ctaClickedAt
      ? hoursSince(record.ctaClickedAt)
      : null,
    lesson_type_id: lessonTypeId ?? null,
    lesson_pack_count: lessonPackCount,
  })
}
